import React from 'react';

import cl from 'classnames';
import { isEmpty } from 'ramda';
import { useSelector } from 'react-redux';

import Button from 'components/shared/Button';
import Icon from 'components/shared/Icon';
import DateOrDay from 'components/shared/Icon/icons/DateOrDay';
import Down from 'components/shared/Icon/icons/Down';
import Tooltip, { TooltipText } from 'components/shared/Tooltip';
import { EOrganizedBy } from 'detailsv2/structure';
import useMediaBreakpoints from 'hooks/useMediaBreakpoints';
import { featureFlagsSelector } from 'store/features/selectors';
import { isDefined } from 'utils/ramda';

import ActionsMenu from '../../../ActionsMenu';
import ActionsMenuCircledButton from '../../../shared/ActionsMenuCircledButton';
import { ChooseStartDate } from '../../ChooseStartDate';
import { useChronologicalMode, useAddEventMenuActions } from '../../functions';
import { CollapseButton } from '../../HeaderActionsButton/CollapseButton';
import { MagicImportButton } from '../../HeaderActionsButton/MagicImportButton/MagicImportButton';
import { useTimelineContext } from '../../TimelineContext';
import { ActionRefs } from '../EventCalendar.types';
import s from './index.module.css';
import { HeaderButton } from './shared/HeaderButton';

function Header(props: { actionsRefs: ActionRefs }) {
  const { startDate, organizedActions, bulkActions, nextDayToCreate, globalActions, organizedBy } =
    useChronologicalMode();
  const addEventMenuActions = useAddEventMenuActions(nextDayToCreate);
  const { isMiddleScreen, isTabletOrMobile } = useMediaBreakpoints();
  const calendarIconFill = isMiddleScreen ? '#EEEEEE' : 'transparent';
  const { selectedEvents } = useTimelineContext();
  const features = useSelector(featureFlagsSelector);
  const isMagicImportEnabled = features?.magicImport === true;

  return (
    <div className={s.container}>
      <div className={cl(s.containerItem, s.containerLeft)}>
        <div className={cl(s.containerItem, s.containerLeftFirst)}>
          <ActionsMenu actions={addEventMenuActions}>
            <Button className={s.addButton}>Add Event</Button>
          </ActionsMenu>

          <CollapseButton withText={!isMiddleScreen} />
        </div>

        <div className={cl(s.containerItem)}>
          {organizedBy === EOrganizedBy.Date && <ChooseStartDate value={startDate as any} isDisabled={!startDate} />}
        </div>
      </div>

      <div className={cl(s.containerItem, s.containerRight)} ref={props.actionsRefs.btnRef}>
        {isMagicImportEnabled && <MagicImportButton />}

        {!isTabletOrMobile && (
          <ActionsMenu
            actions={bulkActions}
            childrenWrapperClassName={s.bulkActionsWrapper}
            className={s.bulkActionsMenu}
            optionsClassName={s.bulkActionsOption}
            disabled={isEmpty(selectedEvents)}
            menuRef={props.actionsRefs.menuRef}
          >
            <HeaderButton
              className={s.bulkActions}
              rightIcon={
                <Icon component={Down} className={cl(s.organizedByDown)} width="6" height="12" fill="#3f3c43" />
              }
              text="Actions"
              data-disabled={isEmpty(selectedEvents)}
              data-tip
              data-for="bulkActions"
            />
            <Tooltip disable={isDefined(selectedEvents)} id="bulkActions" variant="dark">
              <TooltipText>Use Shift + click to select events</TooltipText>
            </Tooltip>
          </ActionsMenu>
        )}

        <ActionsMenu actions={organizedActions} className={s.organizeMenu} optionsClassName={s.organizeMenuOption}>
          <HeaderButton
            leftIcon={<Icon component={DateOrDay} className={cl(s.icon)} fill={calendarIconFill} />}
            rightIcon={<Icon component={Down} className={cl(s.organizedByDown)} width="6" height="12" fill="#3f3c43" />}
            isTextVisible={!isMiddleScreen}
            text={organizedActions.find((action) => action.id === organizedBy)?.title || ''}
          />
        </ActionsMenu>

        <ActionsMenuCircledButton
          actions={globalActions}
          iconClassName={s.dots}
          actionsMenuClassName={s.dropdown}
          optionsClassName={s.dropdownOption}
        />
      </div>
    </div>
  );
}

export default Header;
